import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Row, Col, Alert } from 'react-bootstrap';
import apiService from '../services/api';

const Features = ({ solutionId, solutionName, onBack }) => {
    const [features, setFeatures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentFeature, setCurrentFeature] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        description: '',
        is_active: 'true'
    });

    useEffect(() => {
        fetchFeatures();
    }, [solutionId]);

    const fetchFeatures = async () => {
        try {
            setLoading(true);
            const response = await apiService.get(`/feature?solution_id=${solutionId}`);
            console.log('Fetched features:', response.data);
            setFeatures(response.data);
            setError(null);
        } catch (err) {
            setError('Failed to load features. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const resetForm = () => {
        setFormData({
            name: '',
            code: '',
            description: '',
            is_active: 'true'
        });
    };

    const handleAddFeature = async () => {
        try {
            await apiService.post('/feature', {
                ...formData,
                solution_id: solutionId
            });
            fetchFeatures();
            setShowAddModal(false);
            resetForm();
        } catch (err) {
            setError('Failed to add feature. Please try again.');
            console.error(err);
        }
    };

    const handleEditClick = (feature) => {
        setCurrentFeature(feature);
        setFormData({
            name: feature.name,
            code: feature.code,
            description: feature.description,
            is_active: String(feature.is_active)
        });
        setShowEditModal(true);
    };

    const handleUpdateFeature = async () => {
        try {
            await apiService.put(`/feature/${currentFeature.id}`, {
                ...formData,
                solution_id: solutionId
            });
            fetchFeatures();
            setShowEditModal(false);
        } catch (err) {
            setError('Failed to update feature. Please try again.');
            console.error(err);
        }
    };

    return (
        <div className="p-4">
            <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                        <Button variant="outline-secondary" size="sm" className="me-3" onClick={onBack}>
                            <i className="bi bi-arrow-left me-1"></i>Back to Solutions
                        </Button>
                        <h4 className="mb-0">Features for: {solutionName}</h4>
                    </div>
                    <Button variant="primary" onClick={() => {
                        resetForm();
                        setShowAddModal(true);
                    }}>
                        <i className="bi bi-plus-circle me-2"></i>Add Feature
                    </Button>
                </Card.Header>
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}

                    {loading ? (
                        <div className="text-center py-4">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <Table responsive striped hover>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Code</th>
                                    <th>Description</th>
                                    <th>Active</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {features.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center">No features found</td>
                                    </tr>
                                ) : (
                                    features.map((feature) => (
                                        <tr key={feature.id}>
                                            <td>{feature.name}</td>
                                            <td>{feature.code}</td>
                                            <td>{feature.description}</td>
                                            <td>
                                                <span className={`badge bg-${feature.is_active == true ? 'success' : 'secondary'}`}>
                                                    {feature.is_active == true ? 'Yes' : 'No'}
                                                </span>
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    onClick={() => handleEditClick(feature)}
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </Card.Body>
            </Card>

            {/* Add Feature Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Add New Feature</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Active</Form.Label>
                                <Form.Select
                                    name="is_active"
                                    value={formData.is_active}
                                    onChange={handleInputChange}
                                >
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </Form.Select>
                            </Form.Group>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleAddFeature}>
                        Add Feature
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Edit Feature Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Feature</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Form.Group>
                        </Row>
                        <Row className="mb-3">
                            <Form.Group as={Col}>
                                <Form.Label>Active</Form.Label>
                                <Form.Select
                                    name="is_active"
                                    value={formData.is_active}
                                    onChange={handleInputChange}
                                >
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </Form.Select>
                            </Form.Group>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleUpdateFeature}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Features;
